import { useMemo, useState } from "react";

import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Drawer } from "@/components/Drawer";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { SearchInput, Select } from "@/components/inputs";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { InviteUserForm } from "@/modules/organisations/InviteUserForm";
import { ROLE_LABEL, useOrgUsers, useOrganisations, type OrgUser } from "@/modules/organisations/fixtures";

const USER_STATUS: StatusMap = {
  active: { label: "ACTIVE", color: "var(--ok)" },
  deactivated: { label: "DEACTIVATED", color: "var(--t-mute)" },
};

const COLS: DataCol<OrgUser>[] = [
  { key: "name", label: "Person", width: 170, render: (u) => u.name },
  { key: "login", label: "Email", width: 240, mono: true, render: (u) => u.login_id },
  {
    key: "org", label: "Organisation", width: 110, mono: true,
    // A super admin has NO company — create_user refuses it either way round.
    render: (u) => u.organisation_id ?? <span className="cap">none</span>,
  },
  { key: "role", label: "Role", width: 140, render: (u) => ROLE_LABEL[u.role] },
  {
    key: "access", label: "Space access", width: 230,
    title: (u) => u.space_access.map((s) => `${s.product} · ${s.space}`).join(", "),
    render: (u) => u.space_access.length === 0
      ? <span className="cap">none granted</span>
      : (
        <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {u.space_access.map((s) => <Chip key={`${s.product}:${s.space}`} tone="mute">{s.space}</Chip>)}
        </span>
      ),
  },
  { key: "mfa", label: "MFA", width: 70, render: (u) => u.mfa ? <Chip tone="ok">on</Chip> : <Chip tone="warn">off</Chip> },
  { key: "status", label: "Status", width: 120, render: (u) => <StatusChip st={u.status} map={USER_STATUS} /> },
];

/** Users — Fabric's 12 `users` routes.
 *
 *  A person has ONE role (`super_admin | company_admin | company_user`) and
 *  belongs to one organisation, or to none if they are a super admin. What they
 *  may REACH is space access, granted separately.
 *
 *  Row actions cover the rest of the blueprint: edit · space access · delete ·
 *  deactivate · resend invite · admin reset password · admin reset MFA · send
 *  tool download. **Admin reset MFA is the only MFA recovery path that
 *  exists** — there is no self-service reset, by design.
 */
export function OrgUsersPage() {
  const users = useOrgUsers();
  const orgs = useOrganisations();
  const [search, setSearch] = useState("");
  const [org, setOrg] = useState("");
  const [role, setRole] = useState("");
  const [sel, setSel] = useState<string | null>(null);
  const [mode, setMode] = useState<"idle" | "new">("idle");
  const [done, setDone] = useState("");

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (org && u.organisation_id !== org) return false;
      if (role && u.role !== role) return false;
      if (q && !u.name.toLowerCase().includes(q) && !u.login_id.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [users, org, role, search]);

  const person = users.find((u) => u.user_id === sel) ?? null;

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Organisations · Users"
        title="Users"
        desc="People who can sign in. A person belongs to one organisation — or to none, if they are a super admin."
        right={mode === "idle"
          ? <button className="btn teal" onClick={() => { setMode("new"); setDone(""); }}>Add user</button>
          : <button className="btn ghost" onClick={() => setMode("idle")}>← Back</button>}
      />

      {mode === "new" && (
        <div className="panel panel-pad" style={{ marginBottom: 16 }}>
          <div className="section-label">New user</div>
          <InviteUserForm
            organisations={orgs}
            onCancel={() => setMode("idle")}
            onDone={(email) => { setDone(`Invitation sent to ${email}`); setMode("idle"); }}
          />
        </div>
      )}
      {done && <div style={{ marginBottom: 10, fontSize: 12.5, color: "var(--ok)" }}>{done}</div>}

      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <SearchInput placeholder="Search name / email…" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} />
        <Select value={org} onChange={(e) => setOrg(e.target.value)} style={{ minWidth: 200 }}>
          <option value="">All organisations</option>
          {orgs.map((o) => <option key={o.organisation_id} value={o.organisation_id}>{o.name}</option>)}
        </Select>
        <Select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--t-mute)", alignSelf: "center" }}>
          {rows.length} of {users.length}
        </span>
      </div>

      <DataTable
        rows={rows}
        cols={COLS}
        rowKey={(u) => u.user_id}
        stickyFirst
        pageSize={25}
        onRowClick={(u) => setSel(u.user_id)}
        empty={<div className="panel panel-pad cap">Nobody matches these filters.</div>}
      />

      {/* Row actions live in a DRAWER, not five buttons crammed into a cell.
          Five per row is unreadable at a glance, pushes the useful columns off
          screen, and gives a destructive action the same weight as an edit.
          The drawer also has room to say WHAT each one does — "Reset MFA" is
          meaningless until you know it is the only recovery path there is. */}
      {person && (
        <Drawer onClose={() => setSel(null)} width="min(520px,96vw)"
          header={<>
            <span style={{ fontWeight: 700 }}>{person.name}</span>
            <span className="mono cap">{ROLE_LABEL[person.role]}</span>
            <StatusChip st={person.status} map={USER_STATUS} />
          </>}>
          <div className="section-label">Identity</div>
          <KV k="Email" v={person.login_id} />
          <KV k="Organisation" v={person.organisation_id ?? "none — super admins have no company"} mono={false} />
          <KV k="Role" v={ROLE_LABEL[person.role]} mono={false} />
          <KV k="Two-factor" v={person.mfa ? "enrolled" : "not enrolled"} mono={false} />
          <KV k="Added" v={person.created_at} />
          <KV k="Added by" v={person.created_by} mono={false} />
          <KV k="Last signed in" v={person.last_seen ?? "never"} mono={false} />

          <div className="section-label tight">Space access</div>
          {person.space_access.length === 0
            ? <div className="cap">Nothing granted — this person can sign in but cannot enter a product.</div>
            : person.space_access.map((s) => (
                <KV key={`${s.product}:${s.space}`} k={s.product} v={s.space} mono={false} />
              ))}
          <div style={{ marginTop: 10 }}>
            <button type="button" className="btn ghost sm">Grant space access</button>
          </div>

          <div className="section-label tight">Manage</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn teal">Edit details</button>
            <button type="button" className="btn ghost sm">Resend invitation</button>
            <button type="button" className="btn ghost sm">Send tool download link</button>
          </div>

          <div className="section-label tight">Account recovery</div>
          <div className="cap" style={{ marginBottom: 8 }}>
            Both send the person a link. <strong>Reset MFA is the only way back in</strong> when somebody
            loses their authenticator — there is no self-service reset, by design.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn ghost sm">Reset password</button>
            <button type="button" className="btn ghost sm">Reset MFA</button>
          </div>

          <div className="section-label tight">Danger</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn ghost sm">
              {person.status === "active" ? "Deactivate" : "Reactivate"}
            </button>
            <button type="button" className="btn ghost sm" style={{ color: "var(--bad)" }}>Delete user</button>
          </div>
        </Drawer>
      )}
    </div>
  );
}
